import React from "react";
import { Sparkles } from "lucide-react";
import { ROLE_LABELS, ROLE_COLORS, isCustomRole } from "@/lib/auth.jsx";

/**
 * Unified role pill. For built-in roles it renders the standard colour pill.
 * For admin-created custom roles it adds a tiny "✦ Custom" sub-tag so users
 * can immediately tell them apart from the built-in hierarchy.
 *
 * Pass `customRoles` (array from /admin/custom-roles) to resolve the proper
 * label for custom keys — falls back to the slug if not provided.
 */
export default function RoleBadge({ role, customRoles = [], className = "", size = "sm", showCustomTag = true, dataTestid }) {
  if (!role) return null;
  const custom = isCustomRole(role);
  const meta = customRoles.find((c) => c.role_key === role);
  const label = ROLE_LABELS[role] || meta?.label || role;
  const color = ROLE_COLORS[role] || "bg-slate-100 text-slate-700";
  const sizeClass = size === "xs" ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2.5 py-1";

  return (
    <span className={`inline-flex items-center gap-1 ${className}`} data-testid={dataTestid}>
      <span className={`role-pill ${color} ${sizeClass}`}>{label}</span>
      {custom && showCustomTag && (
        <span
          className="inline-flex items-center gap-0.5 text-[9px] uppercase font-black tracking-wider px-1.5 py-0.5 rounded-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white shadow-sm"
          title="Custom role created by Admin"
          data-testid="role-custom-tag"
        >
          <Sparkles className="w-2.5 h-2.5" /> Custom
        </span>
      )}
    </span>
  );
}
